import { useEffect, useRef, useState, type ReactNode } from 'react'

/* Shared primitives, kept in one file because there are few of them and they
   are only meaningful together. */

export function Panel({
  title,
  note,
  children,
}: {
  title?: string
  note?: string
  children: ReactNode
}) {
  return (
    <section className="panel">
      {title && (
        <div className="panel-h">
          <h3>{title}</h3>
          {note && <span className="note">{note}</span>}
        </div>
      )}
      {children}
    </section>
  )
}

/* ---------------------------- card marks ---------------------------- */

/**
 * The six marks a real deck ships with: four suits and two jokers. Six is also
 * how many sections this console has, so every section gets its own rather than
 * cycling the four suits and repeating two of them.
 *
 * Drawn rather than typed, because the text glyphs ♠♥♦♣ have wildly different
 * weights and baselines across fonts and would never line up in a tab strip.
 */
export type SuitName =
  | 'spade'
  | 'heart'
  | 'diamond'
  | 'club'
  | 'joker-black'
  | 'joker-red'
  | 'card-back'
  | 'hand'

const SUIT_PATHS: Record<string, string> = {
  spade:
    'M12 2.4C12 2.4 4.2 8.6 4.2 13.3c0 2.6 1.8 4.3 4 4.3 1.2 0 2.3-.5 3-1.4-.2 2.3-1 4-2.3 5.1h6.2c-1.3-1.1-2.1-2.8-2.3-5.1.7.9 1.8 1.4 3 1.4 2.2 0 4-1.7 4-4.3C19.8 8.6 12 2.4 12 2.4Z',
  heart:
    'M12 21.3S3.4 15.2 3.4 9.5C3.4 6.6 5.6 4.7 8 4.7c1.7 0 3.2.9 4 2.3.8-1.4 2.3-2.3 4-2.3 2.4 0 4.6 1.9 4.6 4.8 0 5.7-8.6 11.8-8.6 11.8Z',
  diamond: 'M12 2.2 20.4 12 12 21.8 3.6 12 12 2.2Z',
  club:
    'M12 2.6a3.7 3.7 0 0 0-3 5.9 3.7 3.7 0 1 0-1.6 6.9c1 0 2-.4 2.7-1.1-.2 2.5-1 4.4-2.4 5.6h8.6c-1.4-1.2-2.2-3.1-2.4-5.6.7.7 1.7 1.1 2.7 1.1a3.7 3.7 0 1 0-1.6-6.9 3.7 3.7 0 0 0-3-5.9Z',
}

/* The jester cap: a three-pointed hat with a bell on each point. */
const JOKER_PATH =
  'M12 2.1c-1 0-1.8.8-1.8 1.8 0 .5.2 1 .6 1.3L8.6 8.2 5.9 5.9a1.6 1.6 0 1 0-1.3 1.2l1.7 7.3h11.4l1.7-7.3a1.6 1.6 0 1 0-1.3-1.2l-2.7 2.3-2.2-3c.4-.3.6-.8.6-1.3 0-1-.8-1.8-1.8-1.8ZM6.3 15.9v1.4c0 .6.5 1.1 1.1 1.1h9.2c.6 0 1.1-.5 1.1-1.1v-1.4H6.3Z'

export function Suit({ name, className }: { name: SuitName; className?: string }) {
  /* The deck runs out at seven — four suits, two jokers and the card back — so
     the eighth section takes the whole hand: three cards fanned from a pivot at
     the bottom, which is what a sheet of counts about everybody is. */
  if (name === 'hand') {
    return (
      <svg
        className={`suit black${className ? ' ' + className : ''}`}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        {[-24, 0, 24].map((deg) => (
          <rect
            key={deg}
            x={8.4}
            y={4.6}
            width={7.2}
            height={14}
            rx={1.3}
            transform={`rotate(${deg} 12 18.4)`}
          />
        ))}
      </svg>
    )
  }
  if (name === 'card-back') {
    return (
      <svg
        className={`suit black${className ? ' ' + className : ''}`}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M5.2 2.6h13.6c.9 0 1.6.7 1.6 1.6v15.6c0 .9-.7 1.6-1.6 1.6H5.2c-.9 0-1.6-.7-1.6-1.6V4.2c0-.9.7-1.6 1.6-1.6Zm.9 2.3v14.2h11.8V4.9H6.1Z" />
        <path d="M12 6.4 15.4 12 12 17.6 8.6 12 12 6.4Z" />
      </svg>
    )
  }
  const joker = name.startsWith('joker')
  const red = name === 'heart' || name === 'diamond' || name === 'joker-red'
  return (
    <svg
      className={`suit ${red ? 'red' : 'black'}${className ? ' ' + className : ''}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path d={joker ? JOKER_PATH : SUIT_PATHS[name]!} />
    </svg>
  )
}

/** Every section leads with its mark, so the suit is how you know where you are. */
export function ViewTitle({ suit, children }: { suit: SuitName; children: ReactNode }) {
  return (
    <h2 className="view-title">
      <Suit name={suit} className="title-suit" />
      {children}
    </h2>
  )
}

export function Tile({ k, v, d }: { k: string; v: ReactNode; d?: ReactNode }) {
  return (
    <div className="tile">
      <div className="k">{k}</div>
      <div className="v num">{v}</div>
      {d && <div className="d">{d}</div>}
    </div>
  )
}

const STATUS_TEXT: Record<string, string> = {
  critical: '◆ urgent',
  serious: '▲ attention',
  warning: '● watch',
  good: '✓ healthy',
}

export function StatusChip({ severity }: { severity: string | null }) {
  if (!severity) return null
  return <span className={`status ${severity}`}>{STATUS_TEXT[severity] ?? severity}</span>
}

export function Loading({ what }: { what: string }) {
  return <p className="footnote">Loading {what}…</p>
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error)
  const isAuth = message.includes('UNAUTHORIZED')
  return (
    <div className="panel">
      <div className="panel-h">
        <h3>{isAuth ? 'Sign in to continue' : 'That did not load'}</h3>
      </div>
      <p className="footnote">
        {isAuth
          ? 'Your session has expired or this address is not on the allowlist.'
          : message}
      </p>
    </div>
  )
}

/** Tooltip anchored to the cursor; one instance for the whole page. */
export function useTooltip() {
  const [tip, setTip] = useState<{ x: number; y: number; content: ReactNode } | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !tip) return
    const r = el.getBoundingClientRect()
    let left = tip.x + 14
    let top = tip.y - r.height - 12
    if (left + r.width > window.innerWidth - 8) left = window.innerWidth - r.width - 8
    if (left < 8) left = 8
    if (top < 8) top = tip.y + 18
    el.style.left = `${left}px`
    el.style.top = `${top}px`
  }, [tip])

  const node = (
    <div id="tip" ref={ref} role="tooltip" style={{ opacity: tip ? 1 : 0 }} aria-hidden={!tip}>
      {tip?.content}
    </div>
  )

  const bind = (content: ReactNode) => ({
    onMouseMove: (e: { clientX: number; clientY: number }) =>
      setTip({ x: e.clientX, y: e.clientY, content }),
    onMouseLeave: () => setTip(null),
  })

  return { node, bind, setTip }
}

export const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

export const fmtShort = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'

/**
 * A revenue-chart period as a label: "Sep 26" for 'YYYY-MM', "2026" for 'YYYY'.
 *
 * Shared because the chart draws it on the axis and the Patients list repeats it
 * on the filter chip — two spellings of the same month would read as two
 * different filters.
 */
export function periodLabel(period: string): string {
  if (period.length === 4) return period
  /* A single day from the 14-day chart: "23 Oct 26". */
  if (period.length === 10) {
    return new Date(`${period}T00:00:00Z`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    })
  }
  const [year, month] = period.split('-')
  const name = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-GB', {
    month: 'short',
  })
  return `${name} ${year!.slice(2)}`
}

/* ---------------------------- date picker ---------------------------- */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Days in a month, so 31 June cannot be chosen and February knows leap years. */
function daysIn(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/**
 * A date as three dropdowns: day, month, year.
 *
 * The browser's own date input reports every partial value as you type, so a
 * field that saved on change committed "0002" on the way to "2026" and then
 * fought the cursor — Heather Griffith-Tesch still carries a return date in the
 * year 2. Three lists cannot be half-typed: every state they can be in is a
 * date somebody chose, or empty.
 *
 * Years run from the most recent backwards, because every date recorded here is
 * something that already happened.
 *
 * Emits an ISO date only when all three are set, and null when the day is
 * cleared — "no date" stays a real answer, since most of these fields are
 * optional by design.
 */
export function DateParts({
  value,
  onChange,
  earliestYear = 2018,
  latestYear,
  disabled,
  name,
  required,
}: {
  value: string | null
  onChange?: (iso: string | null) => void
  earliestYear?: number
  latestYear?: number
  disabled?: boolean
  /** Renders a hidden input under this name, so a surrounding form reads it
      through FormData exactly as it read the date input this replaced. */
  name?: string
  required?: boolean
}) {
  const top = latestYear ?? new Date().getFullYear()
  /* Holds its own state so it works uncontrolled inside a form, and follows
     `value` when a parent drives it. */
  const [own, setOwn] = useState<string | null>(value)
  const current = onChange ? value : own
  const parts = current?.slice(0, 10).split('-') ?? []
  const year = parts[0] ? Number(parts[0]) : null
  const month = parts[1] ? Number(parts[1]) : null
  const day = parts[2] ? Number(parts[2]) : null

  const set = (iso: string | null) => {
    setOwn(iso)
    onChange?.(iso)
  }

  const emit = (d: number | null, m: number | null, y: number | null) => {
    if (d == null) return set(null)
    /* A day chosen before the rest defaults to the most recent plausible month
       and year, so picking one value is never rejected for the two it implies. */
    const yy = y ?? top
    const mm = m ?? new Date().getMonth() + 1
    const dd = Math.min(d, daysIn(yy, mm))
    set(`${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`)
  }

  const years: number[] = []
  for (let y = top; y >= earliestYear; y--) years.push(y)
  const maxDay = daysIn(year ?? top, month ?? 1)

  return (
    <span className="dateparts">
      {name && <input type="hidden" name={name} value={current ?? ''} required={required} />}
      <select
        aria-label="Day"
        value={day ?? ''}
        disabled={disabled}
        onChange={(e) => emit(e.target.value ? Number(e.target.value) : null, month, year)}
      >
        <option value="">Day</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <select
        aria-label="Month"
        value={month ?? ''}
        disabled={disabled || day == null}
        onChange={(e) => emit(day, e.target.value ? Number(e.target.value) : null, year)}
      >
        <option value="">Month</option>
        {MONTHS.map((m, i) => (
          <option key={m} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <select
        aria-label="Year"
        value={year ?? ''}
        disabled={disabled || day == null}
        onChange={(e) => emit(day, month, e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">Year</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </span>
  )
}
